import React, { useState } from 'react';
import type { ConfirmationResult } from 'firebase/auth';
import { AuthService, DataService } from '../services/firebase';
import { countries } from '../data/countrycodes';
import './Auth.css';

interface AuthProps {
  onLogin: () => void;
}

type AuthView = 'LOGIN' | 'REGISTER' | 'RESET_PASSWORD';
type LoginMethod = 'email' | 'phone';

export const LoginPage: React.FC<AuthProps> = ({ onLogin }) => {
  const [view, setView] = useState<AuthView>('LOGIN');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [showVerification, setShowVerification] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingUpAccount, setIsSettingUpAccount] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Detect user's country from IP on mount
  React.useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://extreme-ip-lookup.com/json/?key=Oc3p0jeNTpi1inmlfqVW');
        const data = await response.json();
        const detectedCountry = countries.find(c => c.iso2.toUpperCase() === data.countryCode);
        if (detectedCountry) {
          setSelectedCountry(detectedCountry);
        }
      } catch (err) {
        console.log('Error detecting country:', err);
      }
    };
    detectCountry();
  }, []);

  const resetState = () => {
    setError('');
    setMessage('');
    setEmail('');
    setPassword('');
    setName('');
    setTeamName('');
    setPhoneNumber('');
    setVerificationCode('');
    setShowVerification(false);
    setConfirmationResult(null);
  };

  const handleBack = () => {
    if (view === 'REGISTER' || view === 'RESET_PASSWORD') setView('LOGIN');
    resetState();
  };

  const pollForAccountSetup = async (userId: string): Promise<void> => {
    const maxAttempts = 15;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const userDoc = await AuthService.getUser(userId);
        if (userDoc && userDoc.orgIds && userDoc.orgIds.length > 0) {
          return; // Setup complete
        }
      } catch (err) {
        console.log('Polling attempt', i + 1, 'failed:', err);
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    throw new Error('Account setup is taking longer than expected. Please try logging in manually.');
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const fullPhoneNumber = `${selectedCountry.dialCode}${phoneNumber}`;
      
      // Check if container exists
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        throw new Error('Recaptcha container not found. Please refresh and try again.');
      }
      
      // Clear any existing recaptcha widgets
      container.innerHTML = '';
      
      const recaptchaVerifier = AuthService.createRecaptchaVerifier('recaptcha-container');
      const result = await AuthService.loginWithPhone(fullPhoneNumber, recaptchaVerifier);
      setConfirmationResult(result);
      setShowVerification(true);
      setMessage('Verification code sent to your phone!');
    } catch (err: any) {
      console.error('Phone auth error:', err);
      setError(err.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmationResult) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await confirmationResult.confirm(verificationCode);
      const fbUser = result.user;

      // Check if user exists in database
      const userData = await AuthService.getUser(fbUser.uid);
      
      if (!userData) {
        await AuthService.logout();
        throw new Error("User profile not found. Please contact support.");
      }

      // Auth listener will handle loading user data
      onLogin();
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      if (view === 'LOGIN') {
        const user = await AuthService.login(email, password);
        onLogin();
      } else if (view === 'REGISTER') {
        if (!name) throw new Error("Name is required");
        if (!teamName) throw new Error("Team/Organization name is required");
        if (password.length < 6) throw new Error("Password must be at least 6 characters");
        
        // Call TrainingRoom API for registration
        const response = await fetch('https://europe-west2-tr-staging-8964c.cloudfunctions.net/api/user/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            firstName: name,
            teamName,
            phoneNumber: phoneNumber ? `${selectedCountry.dialCode}${phoneNumber}` : '',
            smsfrom: teamName
          })
        });
        
        const result = await response.json();
        
        if (result.status !== 1) {
          throw new Error(result.error?.message || result.error || 'Registration failed');
        }
        
        const userId = result.data.uid;
        setIsLoading(false);
        setIsSettingUpAccount(true);
        
        // Poll for account setup completion
        try {
          await pollForAccountSetup(userId);
          
          // Setup complete, now log in
          setIsLoading(true);
          await AuthService.login(email, password);
          onLogin();
        } catch (pollError: any) {
          setIsSettingUpAccount(false);
          throw pollError;
        }
      } else if (view === 'RESET_PASSWORD') {
        await AuthService.resetPassword(email);
        setMessage('Password reset email sent! Check your inbox.');
        setTimeout(() => setView('LOGIN'), 3000);
      }
    } catch (err: any) {
      console.error(err);
      switch (err.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
          setError('Invalid email or password.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email.');
          break;
        case 'auth/email-already-in-use':
          setError('Email already registered.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please use at least 6 characters.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address format.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        default:
          setError(err.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.dialCode.includes(countrySearch)
  );

  if (showVerification) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>SoloReady</h1>
            <h2>Verify Phone Number</h2>
            <p>Enter the 6-digit code sent to {selectedCountry.dialCode}{phoneNumber}</p>
          </div>

          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          <form onSubmit={handleVerifyCode} className="login-form">
            <div className="form-group">
              <label>Verification Code</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>

            <button type="submit" disabled={isLoading} className="submit-btn">
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowVerification(false);
                setConfirmationResult(null);
                setVerificationCode('');
              }}
              className="link-btn"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {isSettingUpAccount && (
        <div className="login-card">
          <div className="login-header">
            <div className="setup-spinner"></div>
            <h2>Setting up your account...</h2>
            <p>Creating your organization and configuring permissions.</p>
            <p className="setup-wait">This usually takes a few seconds.</p>
          </div>
        </div>
      )}
      
      {!isSettingUpAccount && (
      <div className="login-card">
        <div className="login-header">
          <h1>SoloReady</h1>
          <h2>
            {view === 'LOGIN' ? 'Sign In' :
             view === 'REGISTER' ? 'Register as Admin' :
             'Reset Password'}
          </h2>
          <p>
            {view === 'LOGIN' ? 'Sign in to your SoloReady account' :
             view === 'REGISTER' ? 'Create a new administrator account' :
             'Enter your email to receive a password reset link'}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        {view === 'LOGIN' && (
          <div className="login-method-tabs">
            <button
              className={`tab-btn ${loginMethod === 'email' ? 'active' : ''}`}
              onClick={() => setLoginMethod('email')}
            >
              Email
            </button>
            <button
              className={`tab-btn ${loginMethod === 'phone' ? 'active' : ''}`}
              onClick={() => setLoginMethod('phone')}
            >
              Phone
            </button>
          </div>
        )}

        {loginMethod === 'phone' && view === 'LOGIN' ? (
          <form onSubmit={handlePhoneSubmit} className="login-form">
            <div className="form-group">
              <label>Country</label>
              <div className="country-selector">
                <button
                  type="button"
                  className="country-button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                >
                  {selectedCountry.name} ({selectedCountry.dialCode})
                </button>
                {showCountryDropdown && (
                  <div className="country-dropdown">
                    <input
                      type="text"
                      placeholder="Search countries..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      className="country-search"
                    />
                    <div className="country-list">
                      {filteredCountries.map((country) => (
                        <button
                          key={country.iso2}
                          type="button"
                          onClick={() => {
                            setSelectedCountry(country);
                            setShowCountryDropdown(false);
                            setCountrySearch('');
                          }}
                          className="country-option"
                        >
                          {country.name} ({country.dialCode})
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <div className="phone-input-group">
                <span className="dial-code">{selectedCountry.dialCode}</span>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="1234567890"
                  required
                />
              </div>
            </div>

            <div id="recaptcha-container"></div>

            <button type="submit" disabled={isLoading} className="submit-btn">
              {isLoading ? 'Sending Code...' : 'Send Verification Code'}
            </button>

            <div className="form-links">
              <button type="button" onClick={() => setLoginMethod('email')} className="link-btn">
                Use Email Instead
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleEmailSubmit} className="login-form">
            {view === 'REGISTER' && (
              <>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Team/Organization Name</label>
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Your Company Name"
                    required
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="conormcelvaney@trainingroom.cloud"
                required
              />
            </div>

            {view !== 'RESET_PASSWORD' && (
              <div className="form-group">
                <label>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    required
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '0',
                      color: '#666'
                    }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>
            )}

            <button type="submit" disabled={isLoading} className="submit-btn">
              {isLoading ? 'Processing...' :
               view === 'LOGIN' ? 'Log In' :
               view === 'REGISTER' ? 'Register' :
               'Send Reset Link'}
            </button>

            <div className="form-links">
              {view === 'LOGIN' && (
                <>
                  <button type="button" onClick={() => setView('RESET_PASSWORD')} className="link-btn">
                    Forgot your password?
                  </button>
                  <button type="button" onClick={() => setView('REGISTER')} className="link-btn">
                    New organization? Register as Admin
                  </button>
                </>
              )}
              {(view === 'REGISTER' || view === 'RESET_PASSWORD') && (
                <button type="button" onClick={handleBack} className="link-btn">
                  Back to Login
                </button>
              )}
            </div>
          </form>
        )}
      </div>
      )}\n    </div>
  );
};

// Onboarding page remains the same
export const OnboardingPage: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  return (
    <div className="onboarding-container">
      <h1>Welcome to SoloReady</h1>
      <button onClick={onComplete}>Get Started</button>
    </div>
  );
};
